/**
 * Saved search model holds data regarding a saved search instance
 * Saved searches are part of the shared resource system
 * `fetch` will get you the shared resource bean for the saved search
 * `fetchBean` will get you the actual saved search bean
 * @class
 */
import AssetSetModel from '@netx/core-asset/models/assetSet';
import {types as assetSetTypes} from '@netx/core-asset/properties/assetSets';
import {URL} from '@netx/core-next/url';
import Base from '@netx/core-share/models/sharedResource';

export default class SavedSearch extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object}   defaults                - The defaults for SavedSearch model
	 * @property {object}   defaults.criteria       - The default criteria for SavedSearch
	 * @property {string}   defaults.description    - The default description for SavedSearch (TODO: description should become part of shared resource and removed from this bean)
	 * @property {number}   defaults.matchCriteria  - The default match criteria for SavedSearch
	 * @property {number}   defaults.notifyType     - Notify ( 0 = none, 1 = weekly, 2 = daily, 3 = immediately )
	 * @property {string}   defaults.sortField      - The default sort field for SavedSearch
	 * @property {string}   defaults.sortOrder      - The default sort order for SavedSearch
	 */
	get defaults() {
		return _.extend(super.defaults, {
			criteria: [],
			description: '',
			matchCriteria: 0,
			notifyType: 0,
			sortField: '',
			sortOrder: 0,
		});
	}

	/**
	 * @property {object} methods                  - Methods for this model
	 * @property {method} methods.create           - Create new saved search
	 * @property {method} methods.remove           - Destroy saved search
	 * @property {method} methods.update           - Update saved search
	 *
	 * @property {method} methods.duplicateSearch  - Duplicate saved search
	 * @property {method} methods.fetchBean        - Fetch saved search bean (fetch gets the shared resource)
	 * @property {method} methods.setSearch        - Set search data
	 */
	get methods() {
		return _.extend(super.methods, {
			/**
			 * Create saved search
			 * @method
			 * @private
			 */
			create: function (opts) {
				var d = new $.Deferred();
				// TODO: Need the DAM to return full beans on creation (right now it is sending the full search bean but not the shared resource [which is the one we want])
				// in this case (all shared resource cases) it would be the shared resource and not the underlying search bean itself
				this.setSearch().then(function (data) {
					this.set({id: data.id}, opts);
					this.fetch(opts).then(function () {
						d.resolveWith(this, arguments);
					}, d.reject);
				}, d.reject);
				return d.promise();
			},
			/**
			 * Destroy saved search
			 * @method
			 */
			remove: ['deleteSearch', 'id'],
			/**
			 * Update saved search
			 * @method
			 * @param {object} opts Request options
			 */
			update: function (opts) {
				return ['setSearch', this.self()];
			},

			/**
			 * Duplicate saved search
			 * @method
			 * @param {string} searchName Saved search name
			 * @param {object} opts Request options
			 */
			duplicateSearch: function (searchName, opts) {
				return ['duplicateSearch', searchName, this.id];
			},
			/**
			 * Fetch saved search
			 * @method
			 */
			fetchBean: ['getSearch', 'id'],
			/**
			 * Set search data
			 * @method
			 */
			setSearch: function () {
				return ['setSearch', this.self()];
			},
		});
	}

	/** @type {string} */
	get sharedResourceRoute() {
		return `search/saved/${this.id}`;
	}

	/** @override */
	constructor(attrs, opts = {}) {
		/**
		 * Resource type name
		 * @type {string}
		 */
		opts.resourceName = 'savedSearch';

		super(attrs, opts);
	}

	/**
	 * @method
	 * @param {boolean} fullUrl Return full url
	 * @return {string}
	 */
	getRoute(fullUrl) {
		return (
			((fullUrl && URL.location.split('#')[0]) || '') +
			'#' +
			'search/saved' +
			'/' +
			this.id
		);
	}

	/**
	 * @method
	 * @return {module:asset/models/assetSet}
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.savedSearch,
			ids: [this.id],
			label: this.getLabel(),
			models: [this],
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'SavedSearch';
	}
}
